import { gql } from "@apollo/client";
import { alpha, Paper } from "@mui/material";
import WithTooltip from "atoms/WithTooltip";
import { COLOR_BLACK, COLOR_WHITE, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { SPACING_DENSE } from "helpers/spacings";
import usePseudoStyle from "helpers/usePseudoStyle";
import { defer } from "lodash-es";
import React from "react";

export default function StatsTable({ headerColumns, color = COLOR_WHITE, columns, children }) {
  return (
    <Paper
      style={{
        overflow: "auto",
        maxHeight: `calc(100vh - 100px)`,
        borderRadius: 12,
      }}
    >
      <table
        style={{
          fontSize: "10px",
          fontFamily: FONT_FAMILY_GRAPHIK,
          width: "100%",
          borderSpacing: 0,
          borderCollapse: "separate",
          whiteSpace: "nowrap",
          verticalAlign: "middle",
        }}
      >
        <thead>
          <tr
            style={{
              color: COLOR_WHITE,
              backgroundColor: alpha(COLOR_BLACK, 0.8),
              textTransform: "uppercase",
              height: 34,
            }}
          >
            {headerColumns?.map((column, columnIndex) => (
              <th
                key={columnIndex}
                style={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "inherit",
                  ...(columnIndex === 0 && {
                    left: 0,
                    zIndex: 1,
                    maxWidth: 100,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }),
                  textAlign: columnIndex === 0 ? "start" : "center",
                  padding: SPACING_DENSE,
                  fontWeight: 500,
                }}
              >
                {column}
              </th>
            ))}
            {columns?.map((column, columnIndex) => (
              <th
                key={columnIndex}
                style={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "inherit",
                  ...(columnIndex === 0 && {
                    color,
                  }),
                  textAlign: "center",
                  padding: SPACING_DENSE,
                  fontWeight: 500,
                }}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </Paper>
  );
}

export function StatsTableRow({ children, highlighted = false }) {
  const { className } = usePseudoStyle({
    ":nth-of-type(odd)": `
      background-color: ${COLOR_WHITE_LIGHTER};
    `,
    ":nth-of-type(even)": `
      background-color: #f8f9fa;
    `,
    ":last-of-type": `
      font-weight: 500;
    `,
    ":last-of-type td": `
      border-top: 2px solid ${alpha(COLOR_BLACK, 0.8)};
      background-color: #f1f1f4;
    `,
  });

  return (
    <tr
      className={className}
      style={{
        color: COLOR_BLACK,
        height: 40,
        ...(highlighted && {
          backgroundColor: COLOR_WHITE,
        }),
      }}
    >
      {children}
    </tr>
  );
}

export function StatsTableButtonRow({ children }) {
  return (
    <tr>
      <td
        colSpan={99}
        style={{
          padding: 0,
        }}
      >
        {children}
      </td>
    </tr>
  );
}

export function StatsTableHeaderCell({ children, color }) {
  const { className } = usePseudoStyle({
    ":first-child": `
      position: sticky;
      left: 0;
      background-color: inherit;
    `,
  });

  return (
    <td
      className={className}
      style={{
        color,
        padding: `0 ${SPACING_DENSE}`,
        maxWidth: 100,
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </td>
  );
}

export const StatsTableStatCellFragment = gql`
  fragment StatsTableStatCellFragment on Stat {
    statType
    value
    made
    total
  }
`;

export function StatsTableStatCell({ stat, tooltip }) {
  return (
    <StatsTableCell>
      <WithTooltip tooltip={tooltip}>
        {(() => {
          if (stat?.statType === "simple") return <>{stat.value}</>;
          if (stat?.statType === "percentage") {
            const { made, total } = stat;
            const percentage = total > 0 ? Math.round((made * 100) / total) : 0;
            return (
              <div style={{ textAlign: "center" }}>
                <div>
                  {made} - {total}
                </div>
                <div
                  style={{
                    color: alpha(COLOR_BLACK, 0.5),
                    fontSize: "9px",
                    paddingTop: 4,
                  }}
                >
                  {percentage}%
                </div>
              </div>
            );
          }

          if (stat) {
            defer(() => {
              throw new Error(`Unknown stat type: ${stat.statType}`);
            });
            return null;
          }
        })()}
      </WithTooltip>
    </StatsTableCell>
  );
}

export function StatsTableCell({ children }) {
  return (
    <td
      style={{
        padding: `0 ${SPACING_DENSE}`,
        height: 30,
        textAlign: "center",
      }}
    >
      {children}
    </td>
  );
}
